// src/pages/ProfilePage.jsx
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

import api from "api/api";
import endpoint from "api/endpoints";
import { Breadcrumb, Loading } from "common";

import { ProfileSetupWelcome } from "@/components/profile";

export default function ProfilePage() {
  const { user, getAccessTokenSilently } = useAuth0();

  // Get customer info and loading state from Redux store
  const customer = useSelector((state) => state.user.info);
  const loading = useSelector((state) => state.user.loading);

  // Local state for customer existence check
  const [customerExists, setCustomerExists] = useState(null); // null = checking, true = exists, false = doesn't exist
  const [checkingCustomer, setCheckingCustomer] = useState(true);

  // Double-check if customer exists by calling the API
  useEffect(() => {
    const checkCustomerExists = async () => {
      if (!user?.email) {
        setCheckingCustomer(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const response = await api.get(
          endpoint.GET_CUSTOMER_BY_EMAIL(user.email),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // If response has data, customer exists
        setCustomerExists(response.data && response.data.length > 0);
      } catch (error) {
        // If API call fails or customer not found, assume doesn't exist
        setCustomerExists(false);
      } finally {
        setCheckingCustomer(false);
      }
    };

    checkCustomerExists();
  }, [user?.email, getAccessTokenSilently]);

  // Show loading while fetching user data or checking customer existence
  if (loading || checkingCustomer) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10">
        <Breadcrumb path={["Home", "Profile"]} />
        <Loading text="Loading profile..." />
      </div>
    );
  }

  // If no customer profile exists in Redux OR API check confirms no customer exists, show profile setup
  if (!customer || customerExists === false) {
    return <ProfileSetupWelcome />;
  }

  // A customer with a record lands on Settings (Oct 14 2026). /profile itself
  // stays the escape hatch above: ProfileSetupGuard and the no-record flow
  // still send people here to create their NetSuite customer.
  return <Navigate to="/profile/settings" replace />;
}
